import { Dispatch } from "@reduxjs/toolkit";
import { api, dev } from "../config/api";
import { GetState } from "../config/store";
import { AuthActionPost, NewUserProps, RefreshTokenTypes } from "../interfaces/auth";
import {
    logInFailure,
    logInRequest as logInRequestReducer,
    logInSuccess,
    logOut,
    refreshTokenRequest as refreshTokenRequestReducer,
    refreshTokenSuccess,
    refreshTokenFailure,
    initRefreshType as initRefreshTypeReducer,
    editUserRequest as editUserRequestReducer,
    editUserSuccess,
    editUserFailure,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFailure,
    updatePasswordRequest,
    updatePasswordSuccess,
    updatePasswordFailure,
    changePasswordRequest,
    changePasswordSuccess,
    changePasswordFailure,
    cleanChangePassword as cleanChangePasswordReducer,
    registerUserRequest, 
    registerUserSuccess, 
    registerUserFailure,
    cleanErrRegister,
    cleanEditUser as cleanEditUserReducer,
    setLoginRedirection as setLoginRedirectionReducer,
    postUserImageRequest,
    postUserImageSuccess,
    postUserImageFailure,
    cleanPostImage as cleanPostImageReducer,
    deleteUserRequest,
    deleteUserSuccess,
    deleteUserFailure,
    reSendValidationRequest,
    reSendValidationSuccess,
    reSendValidationFailure,
    cleanReSendValidation as cleanReSendValidationReducer,
    updateUser as updateUserReducer,
    getAdminBrandRequest,
    getAdminBrandSuccess,
    getAdminBrandFailure,
    cleanAdminBrand as cleanAdminBrandReducer
} from "../reducers/auth";
import jwt from "jwt-decode";
import { UserProps, UserTypes } from "../interfaces";
import { axiosClient } from "../config/axios";
import { catch401 } from "../helpers/actions";
import { datadogRum } from "@datadog/browser-rum";

export const loginRequest = (user: AuthActionPost, role: UserTypes) => (dispatch: Dispatch, getState: GetState) => _loginRequest(user, role, dispatch, getState);
const _loginRequest = async (user: AuthActionPost, role: UserTypes, dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: logInRequestReducer });

        let originPlatform = "";
        switch(role){
            case "admin-brand":
                originPlatform = "admin_brand_platform";
                break;

            case "admin-dealership":
                originPlatform = "admin_dealership_platform";
                break;
            case "admin-branch":
                originPlatform = "admin_branch_platform";
                break;
            default:
                originPlatform = "seller_platform";
                break;
        }

        const response = await axiosClient.post(`${api.users}/access`, user);

        // @ts-ignore
        const { exp } = jwt(response?.data.data.token);

        sessionStorage.setItem("userSession",
            JSON.stringify({
                user: response?.data.data.user,
                token: response?.data.data.token,
                refreshToken: response?.data.data.refresh_token,
                expirationTime: exp,
            })
        );

        datadogRum.setUser({
            id: response?.data?.data?.user?._id,
            name: response?.data?.data?.user?.name,
            email: response?.data?.data?.user?.email,
            profile: response?.data?.data?.user?.profile?.name
        });

        dispatch({type: logInSuccess, payload: {
            user: response?.data.data.user,
            token: response?.data.data.token,
            refreshToken: response?.data.data.refresh_token,
            expirationTime: exp,
        }});

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse);
        if (errorResponse?.errors) return dispatch({ type: logInFailure, payload: errorResponse?.errors });
        if (errorResponse?.code) return dispatch({ type: logInFailure, payload: errorResponse?.code });
        if (errorResponse?.message) return dispatch({ type: logInFailure, payload: errorResponse?.message });
        dispatch({ type: logInFailure, payload: "Error de conexión." });
    }
};



export const logoutUser = () => (dispatch: Dispatch) => _logoutUser(dispatch);
const _logoutUser = async (dispatch: Dispatch) => {
    try {
        dispatch({ type: logOut });
        sessionStorage.removeItem("userSession")
    } catch (err: any) {
        console.log(err);
    }
};



export const refreshTokenRequest = (refreshType?: RefreshTokenTypes) => (dispatch: Dispatch, getState: GetState) => _refreshTokenRequest(refreshType, dispatch, getState);
const _refreshTokenRequest = async (refreshType: RefreshTokenTypes = "", dispatch: Dispatch, getState: GetState) => {
    try {
        const refreshToken = getState().auth.refreshToken;
        if (!refreshToken) return;

        dispatch({ type: refreshTokenRequestReducer, payload: refreshType });

        const response = await fetch(process.env.REACT_APP_API_TOKEN || "", {
            method: "POST",
            headers: {
                'Authorization': `Bearer ${refreshToken}`,
            },
        });

        const json = await response.json();
        if (!json.success) throw json;

        const { data: { token, refresh_token, user } } = json;
        const { exp }: any = jwt(token);

        datadogRum.setUser({
            id: user?._id,
            name: user?.name,
            email: user?.email,
            profile: user?.profile?.name
        });

        dispatch({ 
            type: refreshTokenSuccess, 
            payload: { 
                token: token, 
                refreshToken: refresh_token, 
                expirationTime: exp, 
                refreshType: refreshType,
                user: user
            } 
        });

    } catch (err: any) {
        catch401(err?.error?.statusCode,dispatch);
        console.log("error token",err?.error?.statusCode);
        dispatch({ type: refreshTokenFailure, payload: { err } });
    }
};

export const initRefreshType = () => (dispatch: Dispatch) => _initRefreshType(dispatch);
const _initRefreshType = async (dispatch: Dispatch) => dispatch({type: initRefreshTypeReducer});


export const editUserRequest = (user: UserProps) => (dispatch: Dispatch, getState: GetState) => _editUserRequest(user, dispatch, getState);
const _editUserRequest = async (user: UserProps, dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: editUserRequestReducer });
        const userId = getState().auth.user?._id;

        const response = await axiosClient.patch(`${api.users}/${userId}`, user);

        dispatch({ type: editUserSuccess, payload: response?.data?.data?.user});
    }catch (error: any){
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        dispatch({ type: editUserFailure, payload: errorResponse?.code });
    }
};

export const cleanEditUser = () => (dispatch: Dispatch) => _cleanEditUser(dispatch);
const _cleanEditUser = async (dispatch: Dispatch) => dispatch({ type: cleanEditUserReducer });

export const resetPassword = (email: string) => (dispatch: Dispatch, getState: GetState) => _resetPassword(email, dispatch, getState);
const _resetPassword = async (email: string, dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: resetPasswordRequest });
  
        await axiosClient.post(`${api.users}/reset-password`, {email: email});

        dispatch({type: resetPasswordSuccess, payload: email});
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);

        dispatch({ type: resetPasswordFailure, payload: errorResponse?.code})
    }
};



export const updatePassword = (email: string, code: string, password: string) => (dispatch: Dispatch) => _updatePassword(email, code, password, dispatch)
const _updatePassword = async (email: string, code: string, password: string, dispatch: Dispatch) => {
    try {
        dispatch({ type: updatePasswordRequest });

        await axiosClient.post(`${api.users}/update-password`, {
            email: email,
            code: code,
            password: password
        });

        dispatch({ type: updatePasswordSuccess })
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        dispatch({ type: updatePasswordFailure, payload: errorResponse?.code });
    }
}

export const changePassword = (password: string) => (dispatch: Dispatch) => _changePassword(password, dispatch)
const _changePassword = async (password: string, dispatch: Dispatch) => {
    try {
        dispatch({ type: changePasswordRequest });

        const body = JSON.stringify({ "password": password });

        const response = await axiosClient.post(`${api.users}/change-password`, body);

        dispatch({ type: changePasswordSuccess, payload: response?.data?.data })
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        dispatch({ type: changePasswordFailure, payload: errorResponse?.code });
    }
}

export const cleanChangePassword = () => (dispatch: Dispatch) => _cleanChangePassword(dispatch);
const _cleanChangePassword = async (dispatch: Dispatch) => dispatch({ type: cleanChangePasswordReducer });

export const registerRequest = (newUser: NewUserProps) => (dispatch: Dispatch) => _registerRequest(newUser, dispatch);
const _registerRequest = async (newUser: NewUserProps, dispatch: Dispatch) => {
    try {
        dispatch({ type: registerUserRequest });

        const response = await axiosClient.post(`${api.users}`, newUser);

        dispatch({ type: registerUserSuccess, payload: response?.data.data.user});
        
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        if(errorResponse?.errors) return dispatch({ type: registerUserFailure, payload: errorResponse.errors });
        if(errorResponse?.code) return dispatch({ type: registerUserFailure, payload: errorResponse.code });
        dispatch({ type: registerUserFailure, payload: "Error de conexión." });
    }
};

export const cleanErrorRegister = () => (dispatch: Dispatch) => _cleanErrorRegister(dispatch);
const _cleanErrorRegister = async (dispatch: Dispatch) => dispatch({ type: cleanErrRegister });

export const setLoginRedirection = (route: string) => (dispatch: Dispatch) => _setLoginRedirection(route, dispatch);
const _setLoginRedirection = (route: string, dispatch: Dispatch) => dispatch({type: setLoginRedirectionReducer, payload: route});

export const postUserImage = (formData: FormData) => (dispatch: Dispatch) => _postUserImage(formData, dispatch);
export const _postUserImage = async (formData: FormData, dispatch: Dispatch) => {
    try {
        dispatch({ type: postUserImageRequest });

        const response = await axiosClient.post(`${api.users}/files`, formData);

        dispatch({ type: postUserImageSuccess, payload: response?.data?.data?.file });
        return response?.data?.data?.file;
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        dispatch({ type: postUserImageFailure });
        return "error";
    }
};

export const cleanPostImage = () => (dispatch: Dispatch) => _cleanPostImage(dispatch);
const _cleanPostImage = async (dispatch: Dispatch) => dispatch({ type: cleanPostImageReducer });

export const deleteUser = (id: string) => (dispatch: Dispatch, getState: GetState) => _deleteUser(id, dispatch, getState);
const _deleteUser = async (id: string,dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: deleteUserRequest });

        await axiosClient.delete(`${api.users}/${id}`);

        dispatch({ type: deleteUserSuccess });
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        dispatch({ type: deleteUserFailure });
    }
}

export const reSendValidation = () => (dispatch: Dispatch, getState: GetState) => _reSendValidation(dispatch, getState);
const _reSendValidation = async (dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: reSendValidationRequest });

        await axiosClient.post(`${api.users}/account/resend-verification`);

        dispatch({type: reSendValidationSuccess});
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code,dispatch);
        dispatch({ type: reSendValidationFailure });
    }
};

export const cleanReSendValidation = () => (dispatch: Dispatch) => _cleanReSendValidation(dispatch);
const _cleanReSendValidation = async (dispatch: Dispatch) => dispatch({ type: cleanReSendValidationReducer});

export const updateUser = (data: any) => (dispatch: Dispatch) => _updateUser(data, dispatch);
const _updateUser = async (data: any, dispatch: Dispatch) => dispatch({ type: updateUserReducer, payload: data});

export const getAdminBrand = () => (dispatch: Dispatch, getState: GetState) => _getAdminBrand(dispatch, getState);
const _getAdminBrand = async (dispatch: Dispatch, getState: GetState) => {
    try {
        dispatch({ type: getAdminBrandRequest });

        const response = await axiosClient.get(`${api.users}/brands/by`);

        dispatch({ type: getAdminBrandSuccess, payload: response?.data.data });
    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        console.log(errorResponse?.code);
        catch401(errorResponse?.code, dispatch);
        dispatch({ type: getAdminBrandFailure, payload: errorResponse?.code });
    }
};

export const cleanAdminBrand = () => (dispatch: Dispatch) => _cleanAdminBrand(dispatch);
const _cleanAdminBrand = async (dispatch: Dispatch) => dispatch({ type: cleanAdminBrandReducer });